import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Package,
  MessageSquare,
  LogIn,
  Phone,
  Mail,
  Menu,
  X,
  User,
  Globe,
  Twitter,
  Facebook,
  Instagram
} from 'lucide-react';
import { useUserRole } from '../hooks/useUserRole';
import Chatbot from './Chatbot';
import { logger } from '../utils/logger';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, role, loading } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  // Debug logging
  logger.log('🏠 Layout render:', { 
    user: user?.email, 
    role, 
    loading, 
    currentPath: location.pathname 
  });

  const handleCustomerPortalClick = () => {
    logger.log('🔘 Portal button clicked:', { user: user?.email, role, loading });
    
    if (loading) return;
    
    if (user) {
      // User is logged in, redirect based on role
      if (role === 'admin') {
        logger.log('👨‍💼 Admin clicking portal, going to admin dashboard');
        navigate('/admin-dashboard');
      } else {
        logger.log('👤 Customer clicking portal, going to customer dashboard');
        navigate('/customer-dashboard');
      }
    } else {
      logger.log('❌ No user, going to login');
      // User is not logged in, go to login page
      navigate('/login');
    }
  };

  const getPortalButtonText = () => {
    if (loading) return 'Loading...';
    if (!user) return 'Customer Login';
    return role === 'admin' ? 'Admin Portal' : 'Customer Portal';
  };

  const getPortalButtonIcon = () => {
    if (loading) return null;
    if (!user) return LogIn;
    return User;
  };

  const PortalIcon = getPortalButtonIcon();

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Products', href: '/products', icon: Package },
    { name: 'Contact', href: '/contact', icon: MessageSquare },
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled || mobileMenuOpen
            ? 'bg-gray-100/90 backdrop-blur-md border-b border-gray-200/70'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-3.5 md:py-4">
            {/* Logo - Always visible */}
            <div className="flex-shrink-0 flex items-center min-w-0">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo.png?v=1" 
                  alt="Simpl Processing Logo" 
                  className="h-8 sm:h-9 md:h-10 w-auto rounded-lg"
                  loading="eager"
                />
              </Link>
            </div>

            {/* Desktop Navigation - Show on md and up, hide on smaller */}
            <nav className="hidden md:flex flex-1 justify-center items-center space-x-6 lg:space-x-8 mx-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-2 lg:px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    isActive(item.href)
                      ? 'text-gray-900 bg-gray-100'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="mr-1.5 lg:mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="hidden lg:inline">{item.name}</span>
                </Link>
              ))}
            </nav>

            {/* Right side - Contact info and buttons */}
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 flex-shrink-0">
              {/* Contact info - Show on lg and up, hide on smaller */}
              <div className="hidden lg:flex items-center space-x-3 xl:space-x-4 text-sm text-gray-600">
                <a href="tel:+1201-846-0591" className="flex items-center hover:text-gray-900 whitespace-nowrap">
                  <Phone className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="hidden xl:inline">(201) 846-0591</span>
                  <span className="xl:hidden">846-0591</span>
                </a>
                <a href="mailto:simran@simplprocessing.com" className="flex items-center hover:text-gray-900 whitespace-nowrap">
                  <Mail className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="hidden xl:inline">simran@simplprocessing.com</span>
                  <span className="xl:hidden text-xs">simran@simpl...</span>
                </a>
              </div>
              
              {/* Portal button - Always visible but text adapts */}
              <button
                onClick={handleCustomerPortalClick}
                disabled={loading}
                className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-3 sm:px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 flex items-center gap-1.5 sm:gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm whitespace-nowrap"
              >
                {PortalIcon && <PortalIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />}
                <span className="hidden sm:inline">{getPortalButtonText()}</span>
                <span className="sm:hidden">Login</span>
              </button>
              
              {/* Mobile menu button - Show on md and below */}
              <button
                type="button"
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 flex-shrink-0"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200/60 py-4 bg-white/95 backdrop-blur-sm rounded-b-xl">
              <nav className="space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors ${
                      isActive(item.href)
                        ? 'text-gray-900 bg-gray-100'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                ))}
                <div className="pt-4 border-t border-gray-200">
                          <button
                            onClick={() => {
                              handleCustomerPortalClick();
                              setMobileMenuOpen(false);
                            }}
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-3 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 flex items-center justify-center gap-2 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {PortalIcon && <PortalIcon className="h-4 w-4" />}
                            {getPortalButtonText()}
                          </button>
                  <div className="space-y-2 text-sm text-gray-600">
                    <a href="tel:+1201-846-0591" className="flex items-center px-3 py-2 hover:text-gray-900">
                      <Phone className="h-4 w-4 mr-3" />
                      (201)-846-0591
                    </a>
                    <a href="mailto:simran@simplprocessing.com" className="flex items-center px-3 py-2 hover:text-gray-900">
                      <Mail className="h-4 w-4 mr-3" />
                      simran@simplprocessing.com
                    </a>
                  </div>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">Simpl Processing</h3>
              <p className="text-gray-400 mb-4">
                Professional payment processing solutions for businesses of all sizes. 
                Same-day deposits, competitive rates, and 24/7 support.
              </p>
              <div className="flex space-x-4">
                <a href="tel:+1201-846-0591" className="text-gray-400 hover:text-white">
                  <Phone className="h-5 w-5" />
                </a>
                <a href="mailto:simran@simplprocessing.com" className="text-gray-400 hover:text-white">
                  <Mail className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white">Home</Link></li>
                <li><Link to="/products" className="text-gray-400 hover:text-white">Products</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white">Customer Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-400">
                <li>New York, NY</li>
                <li>(201) 846-0591</li>
                <li>simran@simplprocessing.com</li>
                <li>Mon-Fri 9AM-6PM EST</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8">
            {/* Language and Social Media */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="flex items-center gap-2 text-gray-400">
                <Globe className="h-4 w-4" />
                <span>United States (English)</span>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-sm">
              <div className="text-gray-400">
                <p>&copy; 2025 Simpl Processing. All rights reserved.</p>
              </div>
              <div className="flex flex-wrap justify-end gap-x-6 gap-y-2 text-gray-400">
                <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a>
                <a href="/security" className="hover:text-white transition-colors">Security</a>
                <a href="/compliance" className="hover:text-white transition-colors">Compliance</a>
                <a href="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>

            {/* MSP/ISO Registration Disclosure */}
            <div className="mt-6 pt-6 border-t border-gray-800">
              <p className="text-xs text-gray-500 text-center sm:text-left">
                Simpl Processing Inc is a Registered MSP/ISO of Elavon, Inc. Georgia [a wholly owned subsidiary of U.S. Bancorp, Minneapolis, MN].
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Layout;
